import React, { useState } from 'react'
import { Card, CardContent } from '../../ui/card'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { DatePicker } from '../../ui/date-picker'
import { EventCombobox } from '../../ui/event-combobox'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { useApplicationStore } from '../../../stores/useApplicationStore'
import ReportEndpoints from '../../../endpoints/ReportEndpoints'
import { ReportViewer, useReportViewer } from '../ReportViewer'
import { RoleSelect } from '../RoleSelect'
import { useReportEvent } from '../useReportEvent'
import { rootUrl } from '../reportShared'

export const AbsenceLatePage: React.FC = () => {
  const { events } = useApplicationStore()
  const { viewerProps, openReport, generating, setGenerating } = useReportViewer()

  const [roles, setRoles] = useState<number[]>([])
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()

  const { selectedEventId, eventRoles, loadingRoles, selectEvent } = useReportEvent((start, end) => {
    setStartDate(start)
    setEndDate(end)
  })

  const generate = async () => {
    if (!selectedEventId || roles.length === 0 || !startDate || !endDate) {
      toast.error('Fill in all required fields'); return
    }
    setGenerating(true)
    try {
      const res = await ReportEndpoints.getAbsenceLateReport({
        eventId: parseInt(selectedEventId), roles,
        startDate: startDate.getTime(), endDate: endDate.getTime(), rootUrl,
      })
      openReport('Absence & Late Report', res)
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to generate report')) }
    finally { setGenerating(false) }
  }

  return (
    <>
      <Card className="max-w-3xl">
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label>Event *</Label>
            <EventCombobox events={events} value={selectedEventId} onChange={selectEvent} />
          </div>
          {selectedEventId && (
            <>
              <div className="space-y-2">
                <Label>Roles * (schedulable only)</Label>
                {loadingRoles ? <Loader2 className="h-4 w-4 animate-spin" /> : <RoleSelect roles={eventRoles} selected={roles} onChange={setRoles} schedulableOnly />}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <DatePicker date={startDate} onDateChange={setStartDate} placeholder="Start date" />
                </div>
                <div className="space-y-2">
                  <Label>End Date *</Label>
                  <DatePicker date={endDate} onDateChange={setEndDate} placeholder="End date" />
                </div>
              </div>
            </>
          )}
          <Button onClick={generate} disabled={generating || !selectedEventId}>
            {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : 'Generate Report'}
          </Button>
        </CardContent>
      </Card>
      <ReportViewer {...viewerProps} />
    </>
  )
}
